// Client for the Expy public onboarding API. Four endpoints, all pre-auth, all on the
// same origin family as the widget (never the host page). Types mirror the server
// contracts in expy-health-api/server/routes/public/*.
import { getSessionId } from './session';
import { getAttribution } from './attribution';
import type { Country } from './zipState';

const BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') || '';

export interface Coverage {
  covered: boolean;
  planType: 'PT' | 'SURGERY' | null;
  planName: string | null;
  interimPlanName: string | null;
  recommendBooking: boolean;
  // Set when the surgery was recent and we have no protocol for it (the API's post-op
  // guard): no exercise plan is offered, not even as an interim, and a person sets one up.
  hold?: 'recent-surgery';
}

// GET /public/surgeries: the catalogue's procedures for one body area, by name, plus the
// catch-all row an unlisted answer is recorded as ("Other knee surgery", else "Other
// surgery"). Names are sent back verbatim as surgeryName.
export interface CatalogueSurgery {
  name: string;
  bodyRegionSlug: string | null;
}
export interface SurgeryList {
  bodyRegionSlug: string | null;
  surgeries: CatalogueSurgery[];
  other: CatalogueSurgery | null;
}

export interface Provider {
  id: string;
  fullName: string;
  photoUrl: string | null;
  bio: string | null;
  city: string | null;
  specialties: string[];
  languages: string[];
  yearsInPractice: number | null;
  licensedStates: string[];
  acceptedInsurancePlans: string[];
  bookingUrl: string | null;
  isBookable: boolean;
}

export interface ProvidersResult {
  state: string | null;
  count: number;
  providers: Provider[];
}

export interface Invite {
  // The one-tap account-claim / sign-in link. Carries NO PHI (server-guaranteed);
  // it's the same link emailed to the patient, rendered as the get-the-app QR.
  claimLink?: string;
  expiresAt?: string;
  error?: string;
}
export interface AccountResult {
  patientId?: string;
  planEnrolled?: boolean;
  invite?: Invite;
  existing?: boolean;
  alreadyConverted?: boolean;
}

// Answer payload sent to /public/intake. Only send the keys a step actually changed —
// the server whitelists and never nulls unspecified fields. Server-owned columns
// (coverage/completedAt/conversion) are never sent from here.
export interface IntakePayload {
  email?: string;
  bodyPart?: string;
  bodyPartDetail?: string;
  careType?: 'SURGERY' | 'INJURY' | 'CHRONIC' | 'UNSURE';
  surgeryName?: string;
  surgeryDetail?: string | null; // "Another surgery", in the patient's words (optional)
  surgeryDate?: string; // YYYY-MM-DD
  symptomDuration?: string;
  painLevel?: number;
  goal?: string;
  intent?: 'HAS_PT' | 'WANT_PT' | 'SELF' | 'UNSURE';
  paymentPreference?: 'INSURANCE' | 'CASH' | 'NO_PREFERENCE';
  insuranceProvider?: string;
  therapistGenderPreference?: 'MALE' | 'FEMALE' | 'NON_BINARY' | 'NO_PREFERENCE';
  zip?: string;
  state?: string;
  country?: Country; // 'US' | 'GB' — decides state-licensure relevance + the booking host
  emailConsent?: boolean;
  disclaimerAccepted?: boolean;
  lastStep?: number;
  completed?: boolean;
  source?: Record<string, unknown>;
}

export class RateLimited extends Error {
  constructor(public retryAfterMs = 3000) {
    super('rate_limited');
  }
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    });
  } catch (e) {
    // Network / CORS failure. The most common cause in dev is the widget origin not
    // being in the API's CORS allowlist — surfaced to the caller to show a soft retry.
    throw new ApiError(`network: ${(e as Error).message}`, 0);
  }
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After'));
    throw new RateLimited(Number.isFinite(retryAfter) ? retryAfter * 1000 : 3000);
  }
  if (!res.ok) {
    throw new ApiError(`http_${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

const qs = (params: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') p.set(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export function getCoverage(args: {
  careType?: string;
  bodyPart?: string;
  detail?: string;
  surgery?: string;
}): Promise<Coverage> {
  return request<Coverage>(
    `/public/coverage${qs({
      careType: args.careType,
      bodyPart: args.bodyPart,
      detail: args.detail,
      surgery: args.surgery,
    })}`,
  );
}

export function getSurgeries(args: { bodyPart?: string; detail?: string }, init?: RequestInit): Promise<SurgeryList> {
  return request<SurgeryList>(`/public/surgeries${qs({ bodyPart: args.bodyPart, detail: args.detail })}`, init);
}

// Upsert partial answers. Returns the server-recomputed coverage snapshot.
export function saveIntake(
  payload: IntakePayload,
): Promise<{ sessionId: string; coverage: Coverage }> {
  // Attribution rides every save (idempotent server-side) rather than only the
  // first: partial saves are best-effort and any one of them may be the one
  // that actually lands. Payload spreads last so an explicit caller `source`
  // would still win.
  const attribution = getAttribution();
  const source = attribution
    ? { channel: 'onboarding-widget', attribution }
    : undefined;
  return request(`/public/intake`, {
    method: 'POST',
    body: JSON.stringify({
      sessionId: getSessionId(),
      ...(source ? { source } : {}),
      ...payload,
    }),
  });
}

export function getProviders(args: {
  state?: string;
  insurance?: string;
  bodyPart?: string;
  careType?: string;
  specialty?: string;
}): Promise<ProvidersResult> {
  return request<ProvidersResult>(`/public/providers${qs(args)}`);
}

export function createAccount(args: {
  firstName: string;
  lastName: string;
  dateOfBirth: string; // YYYY-MM-DD
  phone?: string;
  gender?: string;
}): Promise<AccountResult> {
  return request<AccountResult>(`/public/account`, {
    method: 'POST',
    body: JSON.stringify({ sessionId: getSessionId(), ...args }),
  });
}

// Start a Stripe-hosted Checkout for the account this funnel session was
// converted into. The server decides eligibility (region policy, org-covered)
// and owns the success/cancel URLs; we only ever redirect to what it returns.
export function startCheckout(): Promise<{ url: string }> {
  return request<{ url: string }>(`/public/checkout`, {
    method: 'POST',
    body: JSON.stringify({ sessionId: getSessionId() }),
  });
}

// Exchange an emailed subscribe token (trial lifecycle emails) for a hosted
// Checkout URL. POST on purpose: mail scanners prefetch GET links, and a
// prefetch must never mint a Stripe session.
export function exchangeSubscribeToken(token: string): Promise<{ url: string }> {
  return request<{ url: string }>(`/public/checkout/token`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export type CheckoutVerifyStatus = 'active' | 'open' | 'expired' | 'processing';

// Post-checkout server-side verification (the backup to Stripe's webhook —
// "paid but not provisioned" must be impossible). `stripeSessionId` is the
// cs_… id Stripe appends to the success URL.
export function verifyCheckout(
  stripeSessionId: string,
): Promise<{ status: CheckoutVerifyStatus }> {
  return request<{ status: CheckoutVerifyStatus }>(
    `/public/checkout/verify${qs({ session_id: stripeSessionId })}`,
  );
}
